import { IStairConfig } from '@stair-craft/common/util';

import { IStairNorms } from '../../norms';
import { ITurnStepSplit, resolveTurnSplit } from '../shared-pick-in-norm-flight2-count/pick-in-norm-flight2-count';

export const WINDER_COUNT: number = 3;
export const WINDER_ANGLE_DEG: number = 30;
// Inner offset of winder steps from the wall corner pivot — leaves a small "core" so the inner edge
// is at radius=WINDER_INNER_OFFSET (not 0). Required to satisfy СП 54.13330 (former СНиП 2.08.01-89):
// the winder depth measured RESIDENTIAL_NORMS.winderMeasureOffset from the inner edge must reach
// RESIDENTIAL_NORMS.winderMinInnerDepth; with a 30° sector this offset keeps the chord just above that minimum.
export const WINDER_INNER_OFFSET: number = 50;

export interface ILShapeLayout {
    stepCount: number;
    riserHeight: number;
    treadDepth: number;
    flight1Count: number;
    flight2Count: number;
    turnStepCount: number;
}

/**
 * Coupled solution for (stepCount, riserHeight, treadDepth):
 *   - If user fixes stepCount → riserHeight derived → treadDepth from angle (or default)
 *   - Else if user fixes stairAngle → stepCount auto-picked to keep riserHeight in norm,
 *     treadDepth = riserHeight / tan(angle) follows continuously
 *   - Else if user fixes riserHeight → stepCount derived → treadDepth from default
 *   - Else default (ideal riser)
 * Extracted from `calculateLayout` to keep its complexity low.
 */
function resolveStepDimensions(
    config: IStairConfig,
    norms: IStairNorms,
    totalRise: number
): { stepCount: number; riserHeight: number; treadDepth: number } {
    if (config.stepCount != null) {
        const stepCount: number = Math.max(3, config.stepCount);
        const riserHeight: number = totalRise / stepCount;
        const treadDepth: number =
            config.treadDepth ??
            (config.stairAngle != null
                ? riserHeight / Math.tan(config.stairAngle * (Math.PI / 180))
                : Math.max(norms.treadDepthMin, Math.min(norms.treadDepthMax, norms.blondelIdeal - 2 * riserHeight)));
        return { stepCount, riserHeight, treadDepth };
    }
    if (config.stairAngle != null) {
        const tanA: number = Math.tan(config.stairAngle * (Math.PI / 180));
        // Aim for treadDepth = ideal; back-solve riserHeight, then clamp to norm to derive stepCount
        const idealRiser: number = norms.treadDepthIdeal * tanA;
        const clampedRiser: number = Math.max(norms.riserHeightMin, Math.min(norms.riserHeightMax, idealRiser));
        const stepCount: number = Math.max(3, Math.round(totalRise / clampedRiser));
        const riserHeight: number = totalRise / stepCount;
        const treadDepth: number = config.treadDepth ?? riserHeight / tanA;
        return { stepCount, riserHeight, treadDepth };
    }
    const baseRiser: number = config.riserHeight ?? norms.riserHeightIdeal;
    const stepCount: number = Math.max(3, Math.round(totalRise / baseRiser));
    const riserHeight: number = totalRise / stepCount;
    const rawTreadDepth: number = config.treadDepth ?? norms.blondelIdeal - 2 * riserHeight;
    const treadDepth: number = Math.max(norms.treadDepthMin, Math.min(norms.treadDepthMax, rawTreadDepth));
    return { stepCount, riserHeight, treadDepth };
}

export function calculateLayout(config: IStairConfig, norms: IStairNorms): ILShapeLayout {
    const totalRise: number = config.floorHeight + config.floorThickness;

    let { stepCount, riserHeight, treadDepth } = resolveStepDimensions(config, norms, totalRise);

    const turnStepCount: number = config.turnType === 'landing' ? 1 : WINDER_COUNT;
    let remainingTreads: number = stepCount - 1 - turnStepCount;

    // Ensure at least 1 tread per flight
    if (remainingTreads < 2) {
        remainingTreads = 2;
        stepCount = remainingTreads + 1 + turnStepCount;
        riserHeight = totalRise / stepCount;
    }

    // Flight 2 horizontal envelope (opening + room wall)
    const spaceByOpening: number = config.openingWidth - config.stairWidth;
    const spaceByRoom: number = config.roomWidth - config.openingX - config.stairWidth;
    const maxFlight2Run: number = Math.max(0, Math.min(spaceByOpening, spaceByRoom));

    // STRATEGY: flight 2's tread is force-fit to the opening, so its count and the total
    // step count decide riser, tread, Blondel and angle together — `resolveTurnSplit` picks
    // them jointly to land every check in norm (a pinned turnAtStep fixes flight 1).
    const split: ITurnStepSplit = resolveTurnSplit(config, norms, { totalRise, maxFlight2Run, turnStepCount, stepCount });
    const { flight1Count, flight2Count } = split;
    if (split.stepCount !== stepCount) {
        stepCount = split.stepCount;
        riserHeight = totalRise / stepCount;
    }

    // FORCE-FIT: flight 2 attaches to opening edge exactly
    if (maxFlight2Run > 0 && flight2Count > 0) {
        treadDepth = maxFlight2Run / flight2Count;
    }

    return { stepCount, riserHeight, treadDepth, flight1Count, flight2Count, turnStepCount };
}
